import { Field, Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/utils/css";
import { TranslatedText } from "@/components/common/text";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";

export const SelectDropdown = ({
    items = [],
    value,
    setValue,
    label,
    className,
    placeholder = "Select",
    minWidth = 84,
    menuWidth,
    dropUp = false,
    themes = [],
    usePortal = false,
}) => {
    const pxMinWidth = `${minWidth}px`;
    const pxMenuWidth = menuWidth ? `${menuWidth}px` : pxMinWidth;

    const getDisplayText = () => {
        if (value === null || value === undefined) {
            return placeholder;
        }
        const selected = items.find((item) => item.value === value);
        return selected?.label || value;
    };

    const hasValue = value !== null && value !== undefined;
    const isLabelTop = themes.includes("labelTop");
    const isLoadboard = themes.includes("loadboard");

    return (
        <div className={isLabelTop ? "flex flex-col gap-2" : "flex flex-row gap-[10px] items-center relative"}>
            {isLabelTop && label && (
                <TranslatedText className="text-cm-p-text-primary text-[14px] font-medium text-center">{label}</TranslatedText>
            )}
            {!isLabelTop && label && (
                <TranslatedText className={`text-sm font-normal ${isLoadboard ? "text-cm-p-text-primary" : ""}`}>{label}</TranslatedText>
            )}
            <Field
                className={cn("flex flex-col bg-cm-p-surface-container h-[38px] px-4 py-[10px] rounded-[58px]", className)}
                style={isLoadboard ? { width: pxMinWidth } : { minWidth: pxMinWidth }}
            >
                <Listbox value={value} onChange={setValue}>
                    {({ open }) => (
                        <>
                            <ListboxButton className="inline-flex flex-row justify-between items-center gap-1">
                                <span className={`text-sm ${hasValue ? "text-cm-p-text-primary font-medium" : "text-cm-p-text-secondary"}`}>
                                    {getDisplayText()}
                                </span>
                                <ChevronDown
                                    className={cn("opacity-50 text-cm-p-text-primary transition ease-in w-4 h-4", {
                                        "rotate-180": open,
                                    })}
                                />
                            </ListboxButton>
                            <ListboxOptions
                                portal={usePortal}
                                modal={true}
                                anchor={usePortal ? "bottom start" : undefined}
                                className={cn(
                                    "z-40 flex flex-col rounded-lg bg-cm-p-surface py-[2px] px-1 border border-cm-p-outline overflow-auto gap-[2px]",
                                    {
                                        "mt-[18px] -ml-4 max-h-[305px]": isLabelTop,
                                        "max-h-[124px] h-[124px] absolute right-0": !isLabelTop,
                                        "bottom-[46px]": !isLabelTop && dropUp,
                                        "top-4": !isLabelTop && !dropUp,
                                    },
                                )}
                                style={{
                                    boxShadow: "0px 4px 14px -3px rgba(93, 93, 93, 0.08)",
                                    ...(isLoadboard && menuWidth ? { width: pxMenuWidth } : { width: pxMinWidth }),
                                    ...(isLoadboard &&
                                        items.length > 0 && {
                                            minHeight: `${(items.length > 5 ? 5 : items.length) * 38 + 4}px`,
                                        }),
                                }}
                            >
                                {items.map((item) => {
                                    const isSelected = value === item.value;
                                    return (
                                        <ListboxOption
                                            key={item.value}
                                            value={item.value}
                                            className="inline-flex min-h-[38px] pr-2 pl-[10px] items-center cursor-pointer justify-between hover:bg-cm-p-surface-hover data-[focus]:bg-cm-p-surface-hover rounded-lg"
                                        >
                                            <span className="text-cm-p-text-primary font-medium text-sm">{item.label}</span>
                                            {isSelected && <Check className="size-4 text-cm-p-text-secondary" />}
                                        </ListboxOption>
                                    );
                                })}
                            </ListboxOptions>
                        </>
                    )}
                </Listbox>
            </Field>
        </div>
    );
};
